/**
 * A live terminal attached to one managed session.
 *
 * The connection is a dedicated binary WebSocket (ADR-0004), separate from the
 * domain-event channel, so a fast-scrolling build cannot delay an approval
 * notification.
 *
 * Three behaviours here are correctness concerns rather than polish:
 *
 *  1. **Exactly-once input.** Every keystroke carries a (clientId, sequence)
 *     pair and is held in a pending buffer until acknowledged. On reconnect the
 *     unacknowledged tail is resent; the node drops anything it has already
 *     written. Without this, a reconnect can retype a command into a shell.
 *
 *  2. **Output sanitisation.** Bytes from the PTY pass through the sanitiser
 *     before reaching xterm, because a compromised repository can print escape
 *     sequences that write the clipboard or spoof the tab title.
 *
 *  3. **Honest control state.** An OBSERVED session — one Kimene can see but
 *     cannot safely steer — accepts no input and says so. Pretending otherwise
 *     would let an operator type into a void and believe it landed.
 *
 *  4. **A dropped image is completed, not swallowed.** Dragging a screenshot
 *     onto a page whose drop is unhandled makes the browser navigate to the
 *     file, which is how this pane used to answer: the session went away and the
 *     image opened in a tab. The drop now uploads to the session's host and
 *     types back the path, because a path is the only form either CLI can read.
 */

import {
  useEffect,
  useId,
  useRef,
  useState,
  useCallback,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react'
import { Terminal } from '@xterm/xterm'
import { FitAddon } from '@xterm/addon-fit'
import { WebLinksAddon } from '@xterm/addon-web-links'
import '@xterm/xterm/css/xterm.css'
import clsx from 'clsx'
import { api } from '@/api/client'
import { useTheme } from '@/hooks/useTheme'
import { Badge, Button, StatusDot } from '@/components/primitives'
import { OverflowItem, OverflowMenu } from '@/components/menu'
import { activityLabelKey, sessionActivity, type SessionActivity } from '@/lib/sessionActivity'
import type { SessionKind } from '@/types/domain'
import { sessionActivityTone } from '@/lib/tone'
import { useT } from '@/i18n/context'
import { createWatchdog } from '@/lib/liveness'
import { usePaneChord } from './paneChord'
import { wheelVerdict } from './wheel'
import { adoptGeometry, geometrySync } from './geometry'
import { TranscriptOverlay } from './TranscriptOverlay'
import { RecordingOverlay } from './RecordingOverlay'
import {
  MAX_ATTACHMENT_BYTES,
  carriesFiles,
  imagesFrom,
  promptText,
  uploadFailure,
} from './attachments'
import {
  FrameType,
  chunkInput,
  decodeFrame,
  encodeControl,
  encodeInput,
  encodePing,
  encodeResize,
  resyncPending,
  sanitizeOutput,
  shouldConfirmPaste,
  type TerminalStatus,
} from './protocol'
import { useBroadcast } from './broadcast'
import { KeyBar } from './KeyBar'
import { applyModifiers, nextLatch, sequenceFor, spend, type BarKey, type Latch } from './keybar'
import { useCoarsePointer } from '@/hooks/usePointerKind'
import { moveCursor, selectionRange, yankText, type BufferView, type CopyState } from './copymode'
import { useStartup } from './templates'
import './TerminalPane.css'

export type TerminalMode = 'OBSERVE' | 'CONTROL' | 'COLLABORATE' | 'LOCKED'

interface TerminalPaneProps {
  sessionId: string
  mode: TerminalMode
  onModeChange?: (mode: TerminalMode) => void
  /**
   * What this session is called, when the caller knows a better name than an id.
   *
   * The pane draws the only header the session has, so the name has to reach it.
   * There was a second header above this one carrying it, which is what this
   * prop replaced — see the header below.
   */
  title?: string
  /**
   * Opens the session's details, from the identity in the header.
   *
   * Absent, the identity is text rather than a control: a lone pane with no
   * drawer to open must not render a button that does nothing.
   */
  onInspect?: () => void
  /** Whether those details are the ones currently open, so the identity can say
   *  so rather than looking the same either way. */
  inspecting?: boolean
  /**
   * The surrounding slot's own controls — maximise, close — rendered at the end
   * of this header's cluster.
   *
   * They are passed as nodes rather than as callbacks because they are not the
   * pane's concern: it knows nothing about grids, drawers or what closing means
   * to the page. It only knows where a control goes.
   */
  actions?: ReactNode
  /**
   * What the caller already knows this session to be.
   *
   * The pane learns the kind from the status frame, which arrives some hundreds
   * of milliseconds after mount — and on a session that is slow to connect,
   * never. For that whole window the header was offering "Take control" on a
   * session that may have no controllable PTY behind it, which is precisely the
   * claim this product is built not to make; and now that this is the session's
   * only header, a kind that waits for a socket is a kind that is missing from
   * the one place it is stated. The caller has it from the session list, so it
   * says so up front and the socket confirms it.
   *
   * It replaced a plain `readOnly` flag: OBSERVED was the only value that flag
   * could carry, and the header has to name the other two.
   */
  kind?: SessionKind
  /** Whether this session is over. Passed rather than derived: the pane's socket
   *  reports what a session *is doing*, and a session that has ended has no
   *  socket to report anything — the caller holds the record and knows. It
   *  decides which history the pane offers, because the transcript is dropped
   *  when a session is killed and the recording is what outlives it. */
  ended?: boolean
  /**
   * True when this pane is the slot the operator selected.
   *
   * A whole grid of panes mounts in CONTROL at once, so "this pane became
   * typeable" is not on its own a reason to claim the keyboard — the last status
   * frame to land would take it from the pane being driven. Only the selected
   * slot may. A lone pane leaves this unset and is treated as selected.
   */
  focusOnLive?: boolean
  /**
   * What the session is doing, when the caller knows.
   *
   * The pane colours its frame with this. It is passed in rather than derived
   * here because the caller reads it from the session list, and a pane that
   * worked it out for itself could disagree with the dot sitting directly above
   * it — two answers to one question, six pixels apart. Absent, the pane falls
   * back to what it can see on its own socket.
   */
  activity?: SessionActivity
  /**
   * True when this pane is the whole view rather than one tile on a wall.
   *
   * It travels to the server with the pane's viewport claim, because a session's
   * size is arbitrated between every window that has it open and "the smallest
   * viewer wins" is the wrong rule the moment somebody maximises one: a wall
   * tile on another machine then holds the maximised pane at the tile's width.
   * Measured on the deployment as a pane filling the screen rendering a
   * thirteen-row island of text, garbled where the two widths disagreed.
   *
   * Passed rather than measured. A pane cannot tell "I am large" from "I am the
   * view the operator asked for", and the second is the fact that should outrank
   * a tile — a wall of one big tile is still a wall.
   */
  primary?: boolean
}

/** Identifies this pane for the duration of its mount. Stable across reconnects,
 *  which is what makes (clientId, sequence) deduplication work.
 *
 *  Per instance rather than per page: two panes attached at once shared one
 *  deduplication identity while each kept its own sequence counter, so in a
 *  split layout the second pane's frames collided in the gateway's window from
 *  the first keystroke. The mirror hazard is a remount — the id surviving while
 *  the counter restarts at 1 — which is what the resync below exists for. */
function useClientId() {
  const ref = useRef('')
  if (!ref.current) ref.current = crypto.randomUUID().slice(0, 12)
  return ref.current
}

const MAX_PENDING_INPUT = 512

/** How often the pane renews its input lease. The server's TTL is 60s and
 *  nothing else extends it, so this must leave room for a dropped frame: at 20s
 *  a lease survives two consecutive losses. */
const LEASE_HEARTBEAT_MS = 20_000

/* Liveness. The gateway sends an unsolicited status frame every ten seconds
 * even on a session that prints nothing, so silence on this channel is not
 * "the terminal is quiet" — it is "the path is gone". The budget below is two
 * and a half status intervals, which tolerates one lost frame before the peer
 * is asked directly, and a probe left unanswered for ten seconds is a socket
 * the browser is still calling OPEN and will keep calling OPEN forever. */
const TERMINAL_SILENCE_MS = 25_000
const TERMINAL_PROBE_MS = 10_000
const TERMINAL_TICK_MS = 5_000

/**
 * Who this session is, and — where the caller gave it somewhere to go — a way in.
 *
 * A real <button> only when there is something to open, rather than one that is
 * sometimes inert: an inert button still takes a tab stop and still announces
 * itself as a control, which is a promise a lone pane with no details drawer
 * behind it cannot keep.
 */
function Identity({
  onClick,
  pressed,
  title,
  children,
}: {
  onClick?: () => void
  pressed?: boolean
  title?: string
  children: ReactNode
}) {
  if (!onClick) return <div className="k-term__ident">{children}</div>
  return (
    <button
      type="button"
      className="k-term__ident k-term__ident--action"
      onClick={onClick}
      aria-pressed={pressed}
      title={title}
    >
      {children}
    </button>
  )
}

export function TerminalPane({
  sessionId,
  mode,
  onModeChange,
  title,
  onInspect,
  inspecting,
  actions,
  kind: knownKind,
  ended = false,
  focusOnLive = true,
  activity,
  primary = false,
}: TerminalPaneProps) {
  const t = useT()
  const clientId = useClientId()
  /** The wall's numbering, when this pane is on a wall. Inert elsewhere, so a
   *  lone terminal draws no seat and answers no chord. */
  const chord = usePaneChord()
  const seat = chord.numberOf(sessionId)
  const hostRef = useRef<HTMLDivElement>(null)
  /** The header's control cluster, so the keyboard has a documented way out of
   *  the terminal — see the custom key handler below. */
  const controlsRef = useRef<HTMLDivElement>(null)
  const termRef = useRef<Terminal | null>(null)
  const fitRef = useRef<FitAddon | null>(null)
  const wsRef = useRef<WebSocket | null>(null)

  const sequenceRef = useRef(0)
  /** Input sent but not yet acknowledged, replayed on reconnect. */
  const pendingRef = useRef<Map<number, string>>(new Map())
  /** Highest output offset applied, used as the reconnect cursor. */
  const offsetRef = useRef(0)

  const [status, setStatus] = useState<TerminalStatus | null>(null)
  const [connection, setConnection] = useState<'connecting' | 'open' | 'reconnecting' | 'closed'>(
    'connecting',
  )
  const [pendingPaste, setPendingPaste] = useState<{ text: string; lines: number } | null>(null)
  const [pendingLink, setPendingLink] = useState<string | null>(null)
  /**
   * The emulator's palette follows the theme, which its own builder always
   * intended and nothing ever did.
   *
   * `readXtermTheme` resolves every colour from a design token and its comment
   * says the palette follows the theme "in both directions" — but it ran once, in
   * the mount effect, so flipping the theme left every open pane painted in the
   * old one until it was remounted. A comment claiming a behaviour the code does
   * not have is worse than no comment: it stops the next reader from looking.
   */
  const [theme] = useTheme()
  const [termFont, setTermFont] = useState(readTermFontSize)
  useEffect(() => {
    const term = termRef.current
    if (!term) return
    term.options.theme = readXtermTheme()
  }, [theme])

  /**
   * The type size, watched on the root element rather than pushed from the
   * settings panel.
   *
   * The panel writes a token and nothing else, exactly as the theme and density
   * controls do; a pane on another screen — or in another tab reading the same
   * stored preference — has no path back to that component. Watching the
   * attribute is what makes every open pane agree without any of them knowing
   * where the change came from.
   */
  useEffect(() => {
    const root = document.documentElement
    const observer = new MutationObserver(() => setTermFont(readTermFontSize()))
    observer.observe(root, { attributes: true, attributeFilter: ['style', 'data-density'] })
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const term = termRef.current
    if (!term || term.options.fontSize === termFont) return
    term.options.fontSize = termFont
    // The cell metrics changed, so the fit is wrong until it is redone — and the
    // node has to be told, or it keeps writing at the old geometry.
    fitRef.current?.fit()
    syncGeometryRef.current?.()
  }, [termFont])

  /**
   * The history view — the only readable past this pane has — and how it was
   * opened.
   *
   * `search` differs from `read` by one thing: which control the keyboard lands
   * on. It is a mode rather than a second overlay because there is only one
   * record to search, and the pane used to offer a second search that could not
   * reach it: `SearchAddon` sees the emulator's buffer, and these CLIs draw on
   * the alternate screen where that buffer is the visible screen and nothing
   * else. So "find it in the output" searched what was already in front of the
   * operator, while the thing they meant — every turn, tool call and result —
   * sat in the record one menu entry away.
   */
  const [historyOpen, setHistoryOpen] = useState<false | 'read' | 'search'>(false)
  /** True while a file drag is over this pane, so it can say what a drop will do
   *  before the operator commits to it. */
  const [dragging, setDragging] = useState(false)
  /** How deep the drag is. A counter rather than a boolean because dragleave
   *  fires every time the pointer crosses into a child element, and a boolean
   *  makes the affordance flicker off in the middle of the drag. */
  const dragDepth = useRef(0)
  /** The attachment in flight, or what became of the last one. */
  const [attachment, setAttachment] = useState<{
    tone: 'busy' | 'done' | 'error'
    text: string
  } | null>(null)
  /** Ties the pane's description to the group, so both are announced together. */
  const hintId = useId()

  /** What the socket says this session is, or what the caller knew before it
   *  answered. The two cannot disagree for long: the socket wins as soon as it
   *  speaks, and until then the caller's answer is the only one there is. */
  const kind = status?.kind ?? knownKind
  const observed = kind === 'OBSERVED'

  const canType =
    !observed && (mode === 'CONTROL' || mode === 'COLLABORATE') && status?.youAreController === true

  /* -------------------------------------------------------- the key bar

     Only where the primary pointer is a finger. On a pointer the keys are on
     the keyboard, and a row of buttons duplicating Escape and the arrows would
     cost every desktop 48px of terminal to say what the keyboard already says.
     See `keybar.ts` for what a phone is actually missing. */
  const coarse = useCoarsePointer()
  const [ctrlLatch, setCtrlLatch] = useState<Latch>('off')
  const [altLatch, setAltLatch] = useState<Latch>('off')

  /* The latches are read from inside xterm's data handler, which is registered
     once when the terminal is created — so it would otherwise see whatever they
     were on that first render, forever. Same reason `canType` has a ref. */
  const latchRef = useRef({ ctrl: ctrlLatch, alt: altLatch })
  useEffect(() => {
    latchRef.current = { ctrl: ctrlLatch, alt: altLatch }
  }, [ctrlLatch, altLatch])

  /** Takes what is latched and puts each latch back where it belongs — a single
   *  tap is spent, a lock is kept. One place, so a key from the bar and a letter
   *  from the soft keyboard cannot disagree about whether Ctrl was used up. */
  const takeModifiers = useCallback(() => {
    const { ctrl, alt } = latchRef.current
    const used = { ctrl: ctrl !== 'off', alt: alt !== 'off' }
    if (ctrl === 'once') setCtrlLatch('off')
    if (alt === 'once') setAltLatch('off')
    latchRef.current = { ctrl: spend(ctrl), alt: spend(alt) }
    return used
  }, [])

  /* ------------------------------------------------------- send helpers */

  /**
   * Puts the keyboard where the interface says it is.
   *
   * xterm only receives keys through a hidden helper textarea, and the pane
   * never focused it: an operator who reached a live pane by any route other
   * than clicking the black canvas — taking control from the header, switching
   * the slot's mode, dismissing a confirmation — had the keyboard on a button
   * and typed into nothing. That is the "prompt girilen alan aktif olmuyor"
   * report, and it is a focus defect rather than a control-lease one.
   *
   * Focus follows a gesture, never a server frame; see the effect below.
   */
  const focusTerm = useCallback(() => termRef.current?.focus(), [])

  const sendInput = useCallback(
    (data: string) => {
      const ws = wsRef.current
      if (!ws || ws.readyState !== WebSocket.OPEN) return

      const seq = ++sequenceRef.current
      pendingRef.current.set(seq, data)

      // The pending map is bounded. An unbounded one would grow without limit
      // against a node that has stopped acknowledging, turning a stalled session
      // into a memory leak in the operator's browser.
      if (pendingRef.current.size > MAX_PENDING_INPUT) {
        const oldest = pendingRef.current.keys().next().value
        if (oldest !== undefined) pendingRef.current.delete(oldest)
      }

      ws.send(encodeInput(clientId, seq, data))
    },
    [clientId],
  )

  /**
   * A key from the bar: the sequence for it, under whatever is latched.
   *
   * The keyboard is put back afterwards, and that is not tidiness. Every tap on
   * the bar cancels its own pointer event to *avoid* moving focus (see
   * KeyBar.tsx), but a browser that ignores the cancellation, or an operator
   * whose tap landed on the bar's background rather than a key, leaves xterm
   * without the keyboard and the next letter goes nowhere. Asking for it back
   * costs nothing when it was never lost.
   */
  const onBarKey = useCallback(
    (key: BarKey) => {
      if (!canTypeRef.current) return
      const data = sequenceFor(key, takeModifiers())
      if (!data) return
      sendInput(data)
      broadcastRef.current.relay(sessionId, data)
      focusTerm()
    },
    [sendInput, sessionId, takeModifiers, focusTerm],
  )

  const onLatch = useCallback((which: 'ctrl' | 'alt') => {
    if (which === 'ctrl') setCtrlLatch(nextLatch)
    else setAltLatch(nextLatch)
  }, [])

  /* -------------------------------------------------- terminal lifecycle */

  useEffect(() => {
    const host = hostRef.current
    if (!host) return

    const term = new Terminal({
      fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
      fontSize: readTermFontSize(),
      lineHeight: 1.35,
      cursorBlink: true,
      cursorStyle: 'block',
      // Bounded scrollback. Unbounded would let a long build consume the tab's
      // memory; 10k lines is generous and the full stream is recorded anyway.
      scrollback: 10_000,
      allowProposedApi: true,
      // xterm paints glyphs into absolutely positioned spans that carry no
      // meaning to assistive technology; without this the terminal is, to a
      // screen reader, an empty box. Screen-reader mode makes xterm maintain a
      // parallel live region of the rows, which is the only thing here that can
      // actually be read aloud.
      //
      // It is on unconditionally rather than behind a preference. The rejected
      // alternative was a settings toggle: there is no way to detect assistive
      // technology, so a toggle means the operator who needs this discovers a
      // silent terminal first and the setting second — if at all. The cost is
      // bookkeeping proportional to output on a pane that is already streaming
      // that output over a socket.
      screenReaderMode: true,
      // Kimene renders terminal output into a page that also holds an
      // authenticated session, so the emulator must not be allowed to run
      // sequences that reach outside the canvas.
      allowTransparency: false,
      theme: readXtermTheme(),
    })

    const fit = new FitAddon()
    // Links are detected but never opened directly — activation routes through
    // a confirmation, because the URL text and its target need not agree.
    const links = new WebLinksAddon((event, uri) => {
      event.preventDefault()
      setPendingLink(uri)
    })

    term.loadAddon(fit)
    term.loadAddon(links)
    term.open(host)
    fit.fit()

    termRef.current = term
    fitRef.current = fit

    const onData = term.onData((data) => {
      if (!canTypeRef.current) return
      // A latched modifier applies to whatever the soft keyboard delivers next,
      // which is what makes Ctrl+C reachable at all on a device with no Ctrl
      // key: the bar latches, the keyboard sends 'c', and this turns the two
      // into \x03. On a pointer nothing is ever latched and this is identity.
      const data2 = applyModifiers(data, takeModifiers())
      sendInput(data2)
      // And to whoever else is listening. After the local send, never instead of
      // it: the pane being typed into is the one the operator is watching, and it
      // must not depend on the bus to show what they just pressed.
      //
      // The transformed bytes, not the raw keystroke. A broadcast is "send what
      // I am sending"; relaying 'c' to the other panes while this one received
      // \x03 would interrupt one session and type a letter into the rest.
      broadcastRef.current.relay(sessionId, data2)
    })

    // Shift+Tab is how an operator cycles the agent CLI's mode, and it was
    // silently ejecting them from the terminal. `screenReaderMode` makes xterm
    // skip its own preventDefault for unmodified keys, and its keymap sets the
    // ESC[Z sequence for Shift+Tab without marking the event cancelled — so the
    // sequence reached the agent *and* the browser also ran its default
    // backwards focus traversal, moving the keyboard onto the header button.
    // Everything typed afterwards vanished, and the next Space activated that
    // button, releasing the lease the operator had just taken.
    //
    // Cancelling only the default action keeps the sequence: xterm aborts its
    // own handling only on a literal `false`.
    //
    // Rejected alternative: turning screenReaderMode off. The live region is the
    // only part of this pane a screen reader can read at all — see the note on
    // the constructor.
    //
    // F6 is the deliberate keyboard exit. Plain Tab is already cancelled by
    // xterm, so without one this pane would be a keyboard trap — the same
    // accessibility concern screenReaderMode is on for.
    //
    // Shift+F6 leaves the other way. F6 forward and Shift+F6 back is what
    // "cycle regions" has meant on every desktop that has the key, and half a
    // convention is worse than none: an operator who knows F6 tries Shift+F6
    // next, and it used to reach xterm as an unhandled sequence. Forward hands
    // the keyboard to the pane's own controls; backward steps out ahead of the
    // pane, which is where the page's controls are.
    term.attachCustomKeyEventHandler((ev) => {
      if (ev.type !== 'keydown') return true
      if (ev.key === 'F6') {
        ev.preventDefault()
        if (ev.shiftKey) {
          // Nothing to focus forwards into: blurring drops the keyboard onto the
          // document, and Shift+Tab from there reaches whatever precedes the
          // pane. Focusing a specific element would be guessing at a layout this
          // component does not own.
          term.blur()
          return false
        }
        const exit = controlsRef.current?.querySelector('button')
        // Blurring is the fallback if the header ever renders no control at
        // all: it still ends the trap, because Tab from the document body
        // reaches the page's own controls.
        if (exit) exit.focus()
        else term.blur()
        return false
      }
      // F1 and F2 belong to the console, not to the shell.
      //
      // Both had to be keys that are not terminal bindings, and the function row
      // is the only space that qualifies in a browser: Ctrl+P is readline's
      // previous-history, and Ctrl+Shift+P is Firefox's private window, which no
      // amount of preventDefault gets back. F1 is the reference and F2 the
      // palette — the same order every desktop has used for decades — and they
      // sit beside the F6 above, all three console-level rather than borrowed.
      //
      // Returning false only stops xterm from sending it on; the DOM event still
      // bubbles, which is how the screen above hears it. Nothing is dispatched
      // from here, so a screen with no palette simply swallows one key.
      if (ev.key === 'F1' || ev.key === 'F2') {
        ev.preventDefault()
        return false
      }
      // The Cmd keys this pane lets past, taking the same route as F1 and F2:
      // stopped here so xterm sends nothing, still bubbling so whatever answers
      // them hears it. Cmd+I and Cmd+Esc are answered on the pane's own element;
      // Cmd+M is answered by the slot around it, which is the only thing that
      // owns the maximised state.
      //
      // Cmd is the only modifier a terminal has no sequence for, which is what
      // makes these safe where a Ctrl chord would not be — Ctrl+I is Tab and
      // Ctrl+S is XOFF, and binding either would change what a keypress does
      // inside a session somebody is driving.
      if (
        ev.metaKey &&
        (ev.key === 'i' ||
          ev.key === 'I' ||
          ev.key === 'm' ||
          ev.key === 'M' ||
          ev.key === 'Escape')
      ) {
        ev.preventDefault()
        return false
      }
      // Copy mode owns the keyboard while it is on. Everything is consumed,
      // including the keys it does not use — see the note where it is declared.
      if (copyRef.current) {
        ev.preventDefault()
        handleCopyKeyRef.current(ev)
        return false
      }
      if (ev.key === 'Tab' && ev.shiftKey) ev.preventDefault()
      return true
    })

    // What a notch means is decided in wheel.ts, and deliberately not here — the
    // case that matters is the alternate screen, which no test that mounts this
    // component can reach.
    //
    // Rejected alternative: `set -g mouse on` in the generated tmux config — it
    // makes scrolling depend on bytes travelling as input, so an observer could
    // not scroll at all, it spends an exactly-once sequence number per notch,
    // and it disables text selection in the pane.
    term.attachCustomWheelEventHandler((ev) => {
      const verdict = wheelVerdict({
        meta: ev.metaKey,
        altScreen: term.buffer.active.type !== 'normal',
        mouseTracking: term.modes.mouseTrackingMode !== 'none',
      })
      if (verdict === 'scroll') return true
      // preventDefault on both other paths, and on the Cmd one it is what stops
      // the browser reading the gesture as zoom.
      ev.preventDefault()
      if (verdict === 'record') setHistoryOpen('read')
      return false
    })

    // Intercept paste so a multi-line block asks before executing — and so an
    // image on the clipboard becomes an attachment instead of nothing at all.
    // Ctrl+V of a screenshot otherwise reaches xterm as empty text: the CLIs
    // read the clipboard of the machine they run on, which is not this one.
    const onPaste = (e: ClipboardEvent) => {
      const images = imagesFrom(e.clipboardData)
      if (images.length > 0) {
        e.preventDefault()
        e.stopPropagation()
        void attachRef.current(images)
        return
      }

      const text = e.clipboardData?.getData('text') ?? ''
      const { confirm, lines } = shouldConfirmPaste(text)
      if (confirm) {
        e.preventDefault()
        e.stopPropagation()
        setPendingPaste({ text, lines })
      }
    }
    host.addEventListener('paste', onPaste, true)

    /**
     * Holds a fitted pane against its left edge.
     *
     * A pane sized to its own container has nothing off to the side, so any
     * horizontal offset it acquires is stale the instant it appears — and the
     * operator cannot undo it, because a fitted pane shows no scrollbar. What
     * they see instead is every line missing its first fifty characters, which
     * is the report this exists for.
     *
     * `overflow: hidden` does not prevent this: it hides the scrollbar and still
     * scrolls when something inside is brought into view — a focus, a selection
     * dragged past the edge, a gesture landing while the geometry is still the
     * other operator's. `overflow: clip` would stop it and was rejected: it
     * makes the pane a non-scroller, so the browser walks up and scrolls the
     * *page* instead, moving the whole console rather than one pane.
     *
     * Not applied while following another client, where the screen is genuinely
     * wider than the pane and scrolling sideways is the only way to read it.
     */
    const pinToLeftEdge = () => {
      if (adoptRef.current) return
      if (host.scrollLeft !== 0) host.scrollLeft = 0
    }
    host.addEventListener('scroll', pinToLeftEdge, { passive: true })

    /**
     * Keeps the emulator and the session the same shape.
     *
     * Normally that means fitting to the container and telling the session about
     * it. But a pane that may not resize the session — because somebody else is
     * driving it — must take the session's geometry instead of its own. Sizing
     * to the container regardless is what garbles the screen: tmux keeps
     * painting at the controller's width, the emulator wraps every line at its
     * own, and from then on each repaint lands a row lower than the last
     * instead of overwriting it.
     */
    const syncGeometry = () => {
      try {
        // One decision for both halves, taken before the fit is applied.
        //
        // Fitting and announcing cannot be decided separately: a fit the session
        // is not told about moves the emulator to a width tmux is not painting
        // at, and from there every line wraps where tmux did not intend and each
        // repaint lands a row lower than the last. That is the garble, and it is
        // what "fit anyway, decide about the announcement afterwards" produces —
        // written that way once, deployed, and seen on three panes at once.
        // What this pane can display, which is the only thing it ever announces
        // and what the server arbitrates with.
        //
        // The fallback order matters. Once the emulator has adopted the
        // session's geometry, `term.cols` is *somebody else's* number: claiming
        // it as this window's capacity would announce a size this box cannot
        // show, the server would size the session to it, and the two panes would
        // be back to correcting each other — the resize war a91f1a7 caused and
        // da78986 reverted. So a fresh measurement first, then the last thing
        // this pane claimed, and the emulator only when there has never been
        // either.
        const proposed = fit.proposeDimensions()
        const capacity = proposed ?? announcedRef.current ?? { cols: term.cols, rows: term.rows }

        const verdict = geometrySync({
          hidden: document.hidden,
          announced: announcedRef.current,
          proposed,
          current: capacity,
          primary: primaryRef.current,
        })
        if (verdict === 'skip') return

        const adopt = adoptRef.current
        if (adopt) {
          // Render what tmux is painting. Announcing still happens below: a
          // visible pane that stopped saying what it can display would never be
          // counted again, and the session would stay at the size of whichever
          // other window last spoke — letterboxed or cropped for good, which is
          // the same defect wearing different clothes.
          term.resize(adopt.cols, adopt.rows)
        } else {
          fit.fit()
          // A pane fitted to its own container has nothing to the left or right
          // of what it shows, so any horizontal offset it is holding is stale —
          // from the width it had while it was following somebody else, or from
          // a drag that ran past the edge. Left alone it survives the re-fit and
          // every line renders with its first fifty characters off-screen, which
          // is what an operator sees as the terminal having "gone wrong" with no
          // way back: there is no scrollbar to put it right once the content
          // fits again.
          host.scrollLeft = 0
        }

        if (verdict !== 'announce') return
        const ws = wsRef.current
        if (ws?.readyState !== WebSocket.OPEN) return
        announcedRef.current = { ...capacity, primary: primaryRef.current }
        ws.send(encodeResize(capacity.cols, capacity.rows, primaryRef.current))
      } catch {
        // fit()/resize() throw while the element is detached during a layout
        // change; the next observation will succeed.
      }
    }
    syncGeometryRef.current = syncGeometry

    // Deferred a frame rather than run inside the observation. `fit()` resizes
    // the emulator, which resizes the element being observed, which is a resize
    // raised from inside a resize callback — Chrome reports that as an uncaught
    // "ResizeObserver loop completed with undelivered notifications" error. It
    // is benign (the browser delivers the rest on the next frame), but it is
    // indistinguishable from a real page error to anything watching for one.
    let queued = 0
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(queued)
      queued = requestAnimationFrame(syncGeometry)
    })
    observer.observe(host)

    /**
     * Coming back to the front, and taking the geometry with it.
     *
     * The record of what was announced has to be cleared, not just re-read: this
     * pane's own size has very likely not changed while it was in the background
     * — the *session's* has, because the other window resized it — and the guard
     * in syncGeometry would then keep this pane silent about the one thing it
     * needs to say. Clearing it makes the return assert once, which is bounded
     * to the transition and cannot become the standing argument between two
     * panes that a resize war is.
     */
    const onVisible = () => {
      if (document.hidden) {
        // Withdraw this window's claim on the session's size.
        //
        // Announcing nothing is not enough. The server keeps what each client
        // can display and sizes the session to the smallest of them, so a claim
        // left behind by a window nobody is looking at goes on constraining the
        // one that is: measured on this deployment as three browsers over nine
        // sessions and an operator's 96-column panes held at 76 by a tab open on
        // another machine. The operator sees terminals that render short of
        // their own box with dead space beside them.
        //
        // Zero columns is the wire's way of saying "I am displaying none of
        // this". The frame has always been ignored by servers that do not know
        // it, which is what makes this safe to send at an older one.
        const ws = wsRef.current
        if (ws?.readyState === WebSocket.OPEN) ws.send(encodeResize(0, 0))
        // And forget what was claimed, so coming back asserts it again rather
        // than suppressing itself as a repeat.
        announcedRef.current = null
        return
      }
      announcedRef.current = null
      syncGeometry()
    }
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      observer.disconnect()
      document.removeEventListener('visibilitychange', onVisible)
      cancelAnimationFrame(queued)
      host.removeEventListener('scroll', pinToLeftEdge)
      host.removeEventListener('paste', onPaste, true)
      onData.dispose()
      termRef.current = null

      // Disposed on a later macrotask, not here.
      //
      // xterm's Viewport constructor ends with a bare `setTimeout(() =>
      // this.syncScrollArea())` (xterm 5.5, lib/xterm.js), and that callback
      // reads `this._renderService.dimensions` with no check that the terminal
      // is still alive. Disposing synchronously inside this window — a pane
      // mounted and unmounted in the same tick, which is every mount under
      // React's StrictMode and any quick open-then-close in production — leaves
      // their timeout to fire against a torn-down core and throw an uncaught
      // TypeError. Ours is queued after theirs, so theirs runs first, on a live
      // terminal. Found by the screenshot suite, which fails a capture if the
      // page threw while rendering it.
      const disposing = term
      setTimeout(() => disposing.dispose(), 0)
    }
    // `takeModifiers` is stable (no dependencies of its own), so naming it here
    // does not re-create the terminal — it is listed because the data handler
    // calls it, and a handler registered once must not silently close over a
    // stale one.
  }, [sendInput, takeModifiers])

  // `canType` is read inside the xterm data handler, which is registered once.
  // A ref keeps it current without re-registering the handler on every change.
  /**
   * Broadcast, if the screen offers it.
   *
   * Through a ref because `onData` is installed once, when the terminal is
   * created, and would otherwise capture the bus from that first render forever —
   * the pane would keep relaying to whatever was listening when it opened.
   */
  const broadcast = useBroadcast()
  const broadcastRef = useRef(broadcast)
  useEffect(() => {
    broadcastRef.current = broadcast
  }, [broadcast])

  /**
   * A pane offers itself as a broadcast target only while it could be typed into
   * by hand.
   *
   * The control lease is what stops two people driving one terminal, and a
   * broadcast that ignored it would be a hole straight through it: keystrokes
   * arriving in a session somebody else holds, from a screen that never showed
   * this operator as its controller. So the same gate governs both — if the
   * keyboard would be refused here, the relay is refused too.
   */
  useEffect(() => {
    if (!canType) return
    return broadcast.register(sessionId, sendInput)
  }, [canType, broadcast, sessionId, sendInput])

  /**
   * Copy mode: a cursor over what the terminal has printed, driven by vim keys.
   *
   * Entered from the header rather than from a chord: every key that could open
   * it is already taken — Ctrl+P is readline's history, Ctrl+Shift+C is the
   * browser's inspector — and inventing one would either collide with the shell
   * or be a key nobody guesses. Once inside, the keyboard
   * is the mode's entirely: the handler swallows what it does not use, because a
   * stray `d` reaching a root prompt while somebody thought they were selecting
   * text is precisely what this must never do.
   */
  const [copy, setCopy] = useState<CopyState | null>(null)
  const copyRef = useRef<CopyState | null>(null)
  useEffect(() => {
    copyRef.current = copy
  }, [copy])

  /** The buffer as the movement code sees it. Absolute rows, so scrollback and
   *  the visible screen are one coordinate space — on the alternate screen, where
   *  these CLIs draw, they are the same thing anyway. */
  const bufferView = useCallback((): BufferView | null => {
    const term = termRef.current
    if (!term) return null
    const buf = term.buffer.active
    return {
      first: 0,
      end: buf.length,
      line: (row) => buf.getLine(row)?.translateToString(false) ?? '',
      rows: term.rows,
    }
  }, [])

  /**
   * Paints the selection, and keeps it equal to what `y` would take.
   *
   * Charwise stays on one row and switches to linewise the moment the cursor
   * leaves it. xterm's `select(col, row, length)` is not documented to wrap across
   * rows, and a selection that renders differently from what it yanks is worse
   * than one that cannot span lines by the character — so the mode says which of
   * the two it is, in the strip, and the two APIs it uses each do exactly one.
   */
  const paintCopy = useCallback(
    (state: CopyState) => {
      const term = termRef.current
      const b = bufferView()
      if (!term || !b) return
      const range = selectionRange(state)
      if (!range) {
        term.select(state.cursor.col, state.cursor.row, 1)
        return
      }
      if (state.linewise || range.from.row !== range.to.row) {
        term.selectLines(range.from.row, range.to.row)
        return
      }
      term.select(range.from.col, range.from.row, range.to.col - range.from.col + 1)
    },
    [bufferView],
  )

  const leaveCopy = useCallback(() => {
    termRef.current?.clearSelection()
    setCopy(null)
  }, [])

  const enterCopy = useCallback(() => {
    const term = termRef.current
    const b = bufferView()
    if (!term || !b) return
    // Where the operator is looking: the last line with anything on it, rather
    // than row zero of a screen that is mostly blank.
    let row = Math.max(b.end - 1, 0)
    while (row > b.first && b.line(row).trim() === '') row--
    const next: CopyState = { cursor: { row, col: 0 }, anchor: null, linewise: false }
    setCopy(next)
    paintCopy(next)
    term.focus()
  }, [bufferView, paintCopy])

  /**
   * One key of copy mode.
   *
   * Through a ref for the same reason the broadcast bus is: the custom key
   * handler is installed once, when the terminal is created, and a closure
   * captured then would be looking at the state from that first render forever.
   */
  const handleCopyKey = useCallback(
    (ev: KeyboardEvent) => {
      const state = copyRef.current
      const b = bufferView()
      if (!state || !b) return

      if (ev.key === 'Escape') {
        leaveCopy()
        return
      }
      if (ev.key === 'v' || ev.key === 'V') {
        // Pressing it again drops the anchor rather than doing nothing, so there
        // is always a way back to just a cursor without leaving the mode.
        const linewise = ev.key === 'V'
        const next: CopyState = state.anchor
          ? { ...state, anchor: null, linewise: false }
          : { ...state, anchor: state.cursor, linewise }
        setCopy(next)
        paintCopy(next)
        return
      }
      if (ev.key === 'y' || (ev.key === 'Enter' && state.anchor)) {
        const text = yankText(state, b)
        void navigator.clipboard?.writeText(text).catch(() => undefined)
        setYanked(text.length)
        leaveCopy()
        return
      }

      const key = ev.ctrlKey && (ev.key === 'u' || ev.key === 'd') ? `ctrl-${ev.key}` : ev.key
      const moved = moveCursor(state, key, b)
      if (!moved) return

      // A charwise selection that leaves its row becomes linewise, because what
      // is painted has to be what is taken. The strip says which it is.
      const next =
        moved.anchor && !moved.linewise && moved.anchor.row !== moved.cursor.row
          ? { ...moved, linewise: true }
          : moved
      setCopy(next)
      paintCopy(next)
      termRef.current?.scrollToLine(Math.max(next.cursor.row - Math.floor(b.rows / 2), 0))
    },
    [bufferView, leaveCopy, paintCopy],
  )
  const handleCopyKeyRef = useRef(handleCopyKey)
  useEffect(() => {
    handleCopyKeyRef.current = handleCopyKey
  }, [handleCopyKey])

  /** How much the last yank took, shown briefly so the operator knows it landed —
   *  a clipboard write is otherwise completely silent. */
  const [yanked, setYanked] = useState<number | null>(null)
  useEffect(() => {
    if (yanked === null) return
    const timer = setTimeout(() => setYanked(null), 2500)
    return () => clearTimeout(timer)
  }, [yanked])

  /**
   * A template's first command, delivered once the shell will accept it.
   *
   * Typed rather than launched — see the note in templates.tsx for why the launch
   * argv stays fixed. Gated on `canType`, which means the socket is open, the
   * session is not observed and this browser holds the control lease: the three
   * conditions under which a keystroke actually arrives. Sending earlier would
   * write into a socket that is not there and lose the command silently.
   *
   * `take` removes the entry, so a reconnect cannot run it a second time.
   */
  const startup = useStartup()
  useEffect(() => {
    if (!canType) return
    const command = startup.take(sessionId)
    if (!command) return
    sendInput(`${command}\r`)
  }, [canType, startup, sessionId, sendInput])

  const canTypeRef = useRef(canType)
  useEffect(() => {
    canTypeRef.current = canType
  }, [canType])

  // `mode` decides whether a (re)connect grabs the input lease, and it is read
  // inside the WS open() closure. The WS effect keys only on sessionId, so
  // without a ref a reconnect after the operator released control would replay
  // the mount-time mode and silently re-acquire the lease they gave up.
  const modeRef = useRef(mode)
  useEffect(() => {
    modeRef.current = mode
  }, [mode])

  /** The last status the server sent, written straight off the socket rather
   *  than from the rendered state: a revival needs to know who holds the lease
   *  *now*, and a value that has been through a render is a value that may be
   *  a throttled minute old. */
  const statusRef = useRef<TerminalStatus | null>(null)

  /**
   * A mode handed down *after* mount still has to reach the server.
   *
   * The acquire above runs inside the socket's open handler, so it only ever
   * speaks for the mode the pane connected with. That was enough while every pane
   * mounted in CONTROL. It is not enough for a wall, where the tiles connect as
   * observers and one of them becomes the operator's by being clicked: without
   * this the pane would show CONTROL, offer a cursor, and hold no lease — the
   * operator clicks a terminal, types, and nothing arrives, with the header
   * saying it should.
   *
   * On a transition only, and never on mount. Firing whenever `mode` is CONTROL
   * would re-acquire on every status frame while somebody else holds the grant,
   * and re-acquiring on mount would duplicate what open() already sent.
   */
  const lastModeRef = useRef(mode)
  useEffect(() => {
    const was = lastModeRef.current
    lastModeRef.current = mode
    if (was === mode) return
    if (mode !== 'CONTROL' && mode !== 'COLLABORATE') return
    const ws = wsRef.current
    if (ws?.readyState !== WebSocket.OPEN) return
    // Noted for the same reason requestControl notes it: a refusal arrives as a
    // status frame, and unexplained is indistinguishable from nothing happening.
    askedForControlRef.current = true
    ws.send(encodeControl('acquire'))
  }, [mode])

  /* ----------------------------------------------------------- geometry */

  /**
   * Whether this pane is following somebody else's geometry.
   *
   * The server refuses a resize from a client that does not hold the grant while
   * another one does — correctly, since an observer must not reflow the
   * controller's terminal. The pane has to honour the same rule on its side:
   * asking for a size that will be refused and then rendering as though it had
   * been granted is how the two ends end up disagreeing about the width of the
   * screen, which is the one disagreement a terminal cannot survive.
   */
  const followsSession =
    status != null && status.controller != null && status.youAreController !== true

  /**
   * Whether this pane may take the keyboard.
   *
   * The server answers it, because the server owns all three rules — the
   * permission, the lock, and *who* the holder is. This used to be inferred here
   * as "somebody holds it, so no", which is wrong on the case the server goes
   * out of its way to allow: an operator's own second connection, or the one
   * they had before a socket dropped, taking their own keyboard back. On a wall
   * that reads as a take-control button that is dead for no reason anybody can
   * see — and an operator cannot tell it from a colleague holding the session.
   *
   * The fallback keeps the old inference for a server that does not send the
   * field, because a pane talking to an older control plane should be no worse
   * off than it was.
   *
   * Deliberately *not* what decides `followsSession` above: whether to adopt the
   * session's geometry is a question about the session, not about who is
   * entitled to type into it. A pane observing a screen somebody else sized must
   * render at that size whoever they are.
   */
  const mayTakeControl =
    status?.canTakeControl ?? !(status?.controller != null && status.youAreController !== true)
  const adoptRef = useRef<{ cols: number; rows: number } | null>(null)
  /** Whether the pane is rendering somebody else's geometry rather than its own.
   *  Held as state as well as a ref because the canvas needs a class for it: an
   *  adopted screen can be wider than this box, and without `overflow: auto`
   *  there is no scrollbar and no way to read the right-hand side. */
  const [adopting, setAdopting] = useState(false)
  /** The claim this connection has already told the server about, so it is not
   *  told again. Cleared on connect, because a fresh socket knows nothing. */
  const announcedRef = useRef<{ cols: number; rows: number; primary: boolean } | null>(null)
  const syncGeometryRef = useRef<() => void>(() => {})
  /** The rank this pane would claim now, read through a ref because the geometry
   *  effect is created once — with `[sendInput, takeModifiers]` for
   *  dependencies — and a closure over the prop would announce the rank the pane
   *  had when it mounted, forever. The same reason `adoptRef` above is a ref. */
  const primaryRef = useRef(primary)
  primaryRef.current = primary
  // Maximising changes what this pane is, not only how large it is, so it has to
  // be said. On a wall of one tile it changes almost nothing else: the box moves
  // by the width of a border, and a sync that compared sizes alone would call
  // that a repeat and leave the server ranking the full-screen pane as a tile.
  useEffect(() => {
    syncGeometryRef.current()
  }, [primary])

  // Keyed on the geometry itself, not on the status object. Every status frame
  // — one every ten seconds, unsolicited — is a freshly parsed object, so an
  // effect keyed on it ran on a timer and dragged a resize along behind it.
  const sessionCols = status?.cols ?? 0
  const sessionRows = status?.rows ?? 0
  useEffect(() => {
    // A pane renders at the *session's* size whenever that is not the size this
    // pane asked for — not only when somebody else is driving.
    //
    // The narrower rule was the bug: with nobody holding control, the server let
    // any viewer size the session, so a session open in two windows took
    // whichever spoke last and the other window went on painting at its own
    // width. Every line then wrapped where tmux did not intend. From that
    // operator's side it happened out of nowhere and survived until they
    // reloaded the page, because reloading re-announces and wins the race.
    const adopted = adoptGeometry({
      session: { cols: sessionCols, rows: sessionRows },
      driven: followsSession,
      announced: announcedRef.current,
    })
    adoptRef.current = adopted
    setAdopting(adopted != null)
    // Called on both transitions: taking the session's shape when somebody else
    // sizes it, and re-fitting to the container the moment the session comes
    // back to this pane's own size, so it does not stay letterboxed after the
    // other window closes.
    syncGeometryRef.current()
  }, [followsSession, sessionCols, sessionRows])

  /* --------------------------------------------------------- attachments */

  const noteTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  /** Says what just happened to a dropped file, and gets out of the way. */
  const showNote = useCallback((tone: 'busy' | 'done' | 'error', text: string) => {
    if (noteTimer.current) clearTimeout(noteTimer.current)
    setAttachment({ tone, text })
    // Work in progress stays until it ends. A result clears itself: this pane's
    // job is the terminal, and a note that never leaves is a permanent tax on
    // the rectangle the operator is actually reading. A failure lingers longer
    // than a success, because it is the one that has to be read to be acted on.
    if (tone !== 'busy') {
      noteTimer.current = setTimeout(() => setAttachment(null), tone === 'error' ? 8000 : 4000)
    }
  }, [])

  /** Whether this pane has asked for control and not yet heard back. */
  const askedForControlRef = useRef(false)

  /**
   * Reads the server's answer to a "take control" request out of the status
   * frame that follows it.
   *
   * There is no reject frame for control — the status frame *is* the answer, and
   * it names the holder. Until this, a refusal looked exactly like a button that
   * did nothing: the pane went on observing and the reason sat in the server's
   * log. The common refusal is not even a colleague; it is the operator's own
   * dropped connection, which the server now takes over from automatically, so
   * what reaches here is the case that genuinely needs a person told.
   */
  const answerControlRequest = useCallback(
    (next: TerminalStatus) => {
      if (!askedForControlRef.current) return
      if (next.youAreController) {
        askedForControlRef.current = false
        return
      }
      if (!next.controller) return // still being decided; the next frame answers
      askedForControlRef.current = false
      showNote('error', t('terminal.controlHeldBy', { actor: next.controller }))
      // The optimistic flip is undone: this pane is an observer, and a header
      // that says otherwise is the same lie in a different place.
      onModeChange?.('OBSERVE')
    },
    [showNote, t, onModeChange],
  )

  useEffect(
    () => () => {
      if (noteTimer.current) clearTimeout(noteTimer.current)
    },
    [],
  )

  /**
   * Uploads each image to the session's host and types back where it landed.
   *
   * Gated on the input lease, not merely on politeness: the file is written into
   * the operator's checkout on a machine they may not be sitting at, which is
   * the same authority as typing into the session. The server enforces this too
   * — this check exists so the refusal arrives before the upload, not after.
   */
  const attachImages = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return
      if (!canTypeRef.current) {
        showNote('error', t('terminal.dropNeedsControl'))
        return
      }

      // Sequential, so the paths reach the prompt in the order they were
      // dropped. Uploading in parallel would hand the operator an order nobody
      // chose, on a line they are about to send to an agent.
      for (const file of files) {
        if (file.size > MAX_ATTACHMENT_BYTES) {
          showNote('error', t('terminal.dropTooLarge'))
          continue
        }
        showNote('busy', t('terminal.dropUploading', { name: file.name }))
        try {
          const res = await api.upload<{ path: string }>(
            `/sessions/${encodeURIComponent(sessionId)}/attachments`,
            file,
            { client: clientId, name: file.name },
          )
          // Typed, never submitted. The path is an ingredient of the operator's
          // next prompt; pressing Enter for them would send a half-composed
          // instruction to an agent that acts on it.
          sendInput(promptText(res.path))
          showNote('done', t('terminal.dropDone', { name: file.name }))
          focusTerm()
        } catch (err) {
          const { key, params } = uploadFailure(err)
          showNote('error', t(key, params))
        }
      }
    },
    [clientId, focusTerm, sendInput, sessionId, showNote, t],
  )

  // Read from inside the paste handler, which is registered once with xterm.
  // Putting the callback in that effect's dependencies would tear down and
  // rebuild the whole terminal every time this identity changed.
  const attachRef = useRef(attachImages)
  useEffect(() => {
    attachRef.current = attachImages
  }, [attachImages])

  /* --------------------------------------------------------------- drag */

  // preventDefault on dragover is the entire reason a drop can be handled here.
  // Without it the browser runs its own default — navigate the tab to the
  // dropped file — which took the session off the screen and opened the image
  // instead. Only file drags are claimed; text dragged within the pane is left
  // to the emulator.
  const onDragOver = (e: React.DragEvent) => {
    if (!carriesFiles(e.dataTransfer)) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'copy'
  }

  const onDragEnter = (e: React.DragEvent) => {
    if (!carriesFiles(e.dataTransfer)) return
    e.preventDefault()
    dragDepth.current += 1
    setDragging(true)
  }

  const onDragLeave = (e: React.DragEvent) => {
    if (!carriesFiles(e.dataTransfer)) return
    dragDepth.current = Math.max(0, dragDepth.current - 1)
    if (dragDepth.current === 0) setDragging(false)
  }

  const onDrop = (e: React.DragEvent) => {
    if (!carriesFiles(e.dataTransfer)) return
    e.preventDefault()
    dragDepth.current = 0
    setDragging(false)

    const images = imagesFrom(e.dataTransfer)
    if (images.length === 0) {
      // Something was dropped and none of it was an image this can carry. Said
      // out loud, because the alternative is a gesture that silently does
      // nothing and reads as a broken pane.
      showNote('error', t('terminal.dropNotImage'))
      return
    }
    void attachImages(images)
  }

  /* ------------------------------------------------ websocket lifecycle */

  useEffect(() => {
    let stopped = false
    let attempt = 0
    let heartbeat: ReturnType<typeof setInterval> | null = null
    let retry: ReturnType<typeof setTimeout> | null = null
    /** The watchdog belonging to the socket currently open, so that the window
     *  listeners below always poke the live one and never a superseded one. */
    let watchdog: ReturnType<typeof createWatchdog> | null = null

    const open = () => {
      if (stopped) return
      setConnection(attempt === 0 ? 'connecting' : 'reconnecting')

      const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      // The cursor lets the node replay only what this client missed, rather
      // than re-sending an entire session's scrollback on every reconnect.
      //
      // Omitted entirely until there is one. A pane that has applied no output
      // has no screen to preserve, and sending `from=0` said the opposite — the
      // server read a resumable cursor, replayed everything it had retained and
      // told the pane not to reset. Absent is the honest way to say "I am new".
      const resume = offsetRef.current > 0 ? `from=${offsetRef.current}&` : ''
      const url = `${proto}//${window.location.host}/ws/terminal/${encodeURIComponent(sessionId)}?${resume}client=${clientId}`
      const ws = new WebSocket(url)
      ws.binaryType = 'arraybuffer'
      wsRef.current = ws

      // One decoder for the whole connection: output frames are cut at byte
      // boundaries upstream, so a multi-byte rune can straddle two frames. A
      // per-frame decoder turns that tail into replacement characters; a
      // persistent streaming decoder holds the partial bytes until the rest
      // arrives. A reconnect calls open() again and gets a fresh decoder, which
      // is correct — it is a new byte stream.
      const decoder = new TextDecoder()
      // One reset per connection, and only if this one is told to.
      const didResetRef = { current: false }
      // A new socket knows nothing about the lease until the server tells it.
      // Carrying the old one's last word across would let a revival conclude
      // "control is already mine" from a fact about a connection that no longer
      // exists — which is the same class of mistake as the freeze itself.
      statusRef.current = null

      ws.onopen = () => {
        attempt = 0
        setConnection('open')
        // Start watching only now. A socket still in CONNECTING has nothing to
        // probe with, and counting its handshake as silence would condemn a
        // slow-but-healthy connection.
        local.start()
        // A fresh connection has been told no geometry, whatever the last one
        // knew, so the announcement below must not be suppressed as a repeat.
        announcedRef.current = null

        // Take the input channel BEFORE announcing our size. The server applies
        // a resize from a non-holder only while no one controls the session, so
        // the lease must be held first — otherwise this first, correct geometry
        // is dropped and the pane stays at its default size, reflowing the
        // program's absolute cursor moves onto the wrong columns (the garble the
        // operator saw). The frames are read in order, and the acquire is applied
        // synchronously, so by the time the resize is read the lease is held.
        // Take the input channel on connect when the operator opened this to
        // drive it, so they can type straight away instead of landing read-only
        // and having to hunt for a "take control" button first. An observed
        // session is pinned to OBSERVE upstream and never reaches here. Read the
        // ref, not the prop, so a reconnect honours the operator's *current*
        // control intent rather than re-grabbing a lease they since released.
        if (modeRef.current === 'CONTROL' || modeRef.current === 'COLLABORATE') {
          ws.send(encodeControl('acquire'))
        }

        // Say what this window can display — through the one place that decides
        // it, rather than by reading the emulator here.
        //
        // Two reasons, and both were defects when this block did it itself. A
        // pane whose window is in the background is displaying nothing, and a
        // claim from it holds the session down for the window that *is* being
        // looked at: reconnecting is exactly when a hidden tab would have made
        // one, because no visibilitychange fires for a window that was already
        // hidden. And `term.cols` is not this window's capacity once the emulator
        // has adopted the session's geometry — it is the other window's number,
        // and announcing it would claim a width this box cannot show.
        //
        // announcedRef was cleared above, so syncGeometry sees a socket that has
        // said nothing and asserts once.
        syncGeometryRef.current()

        // Replay unacknowledged input in order. The node drops anything it has
        // already written, so this cannot double-type.
        for (const [seq, data] of [...pendingRef.current.entries()].sort((a, b) => a[0] - b[0])) {
          ws.send(encodeInput(clientId, seq, data))
        }

        heartbeat = setInterval(() => {
          if (ws.readyState !== WebSocket.OPEN) return
          ws.send(encodePing())

          // Renew the input lease. Nothing else does — the server's registry
          // extends a lease only from this frame, deliberately, so that a client
          // spamming input cannot hold the channel by typing. The pane never
          // sent it, so after the 60-second TTL the gateway silently refused
          // every keystroke while the header still read "control is yours".
          // That is the intermittent half of "kontrolü aldığım halde
          // yazamıyorum": it worked for a minute, then died.
          //
          // Gated on control *intent*, not on status.youAreController: status is
          // only ever written by a server frame, and the first one — sent on
          // connect, before the acquire is read — reports false, which would
          // latch the heartbeat off for the life of the connection. The server
          // no-ops a heartbeat when no lease is held, so an observer's costs a
          // map lookup.
          if (modeRef.current === 'CONTROL' || modeRef.current === 'COLLABORATE') {
            ws.send(encodeControl('heartbeat'))
          }
        }, LEASE_HEARTBEAT_MS)
      }

      ws.onmessage = (ev) => {
        // Before decoding, and for every frame including one this build cannot
        // read: what proves the path is alive is that bytes arrived on it, not
        // what they turned out to mean.
        watchdog?.alive()
        if (!(ev.data instanceof ArrayBuffer)) return
        const frame = decodeFrame(ev.data)

        switch (frame.type) {
          case FrameType.Output: {
            offsetRef.current = frame.offset + frame.data.byteLength
            const text = decoder.decode(frame.data, { stream: true })
            // Pinned to the newest line, always. xterm's own rule is to hold the
            // viewport wherever it is and only follow output when it is already
            // at the bottom — right for a terminal you scroll, wrong for this
            // one: a live pane is a window on what an agent is doing *now*, its
            // scrollbar is deliberately not drawn (scrollbars.css), and reading
            // back is the history view's job. Left to itself a pane that drifted
            // up — a focus, a stray notch, a re-fit — stayed there while the work
            // ran on underneath, which is what an operator sees as output that
            // has stopped arriving.
            //
            // In the write callback rather than after it: write() parses on its
            // own schedule, so scrolling here would scroll to the bottom of the
            // screen as it was *before* these bytes landed. On the alternate
            // screen, which is every agent pane, this is a no-op — there is no
            // scrollback to be away from — and that is the point: it costs
            // nothing where it is not needed and fixes the pane where it is.
            termRef.current?.write(sanitizeOutput(text), () => termRef.current?.scrollToBottom())
            break
          }
          case FrameType.Ack:
            // Acknowledged input can never need resending.
            pendingRef.current.delete(frame.sequence)
            break
          case FrameType.Reject: {
            // The server refused a frame and named the sequence it wants next.
            // Rewind to it and replay the tail from there; entries below it have
            // already been applied. Without this the two counters could never
            // re-converge, and the pane stayed mute — cursor blinking, header
            // still reading "control is yours" — until the page was reloaded.
            const { nextSequence, replay } = resyncPending(
              pendingRef.current,
              frame.sequence,
              frame.expected,
            )
            pendingRef.current.clear()
            sequenceRef.current = nextSequence
            for (const data of replay) sendInput(data)
            break
          }
          case FrameType.Status:
            // Before anything else: the server sends this ahead of the replay it
            // refers to, and applying it late would clear the very output it was
            // sent to make sense of. Guarded on the socket as well as on the flag,
            // because a status frame arrives every ten seconds and wiping the
            // operator's screen on each one would be its own defect.
            if (frame.status.streamReset && !didResetRef.current) {
              didResetRef.current = true
              termRef.current?.reset()
              offsetRef.current = 0
            }
            answerControlRequest(frame.status)
            statusRef.current = frame.status
            setStatus(frame.status)
            break
          default:
            // Ping, or a frame type this client does not know. Either way the
            // terminal keeps working — and the arrival was already counted as
            // liveness above, which is the whole point of the echo. It used to
            // be dropped here, which left the pane pinging a socket nothing was
            // listening on and calling that a heartbeat.
            break
        }
      }

      // Both the peer's close frame and the watchdog's verdict end the
      // connection through here, and the latch makes it happen once. A socket
      // the watchdog condemned usually does fire onclose a moment later, and
      // scheduling two reconnects would leave one of the resulting sockets
      // unreferenced, still streaming, still holding a lease.
      let ended = false
      const end = () => {
        if (ended) return
        ended = true
        if (heartbeat) clearInterval(heartbeat)
        heartbeat = null
        local.stop()
        if (watchdog === local) watchdog = null
        if (stopped) {
          setConnection('closed')
          return
        }
        setConnection('reconnecting')
        // Backoff with jitter so a server restart does not produce a
        // synchronised reconnect from every open terminal at once.
        const base = Math.min(500 * 2 ** attempt, 15_000)
        attempt += 1
        retry = setTimeout(open, base * (0.5 + Math.random() * 0.5))
      }

      const local = createWatchdog({
        silenceMs: TERMINAL_SILENCE_MS,
        probeMs: TERMINAL_PROBE_MS,
        tickMs: TERMINAL_TICK_MS,
        probe: () => {
          if (ws.readyState === WebSocket.OPEN) ws.send(encodePing())
        },
        onDead: () => {
          // Reconnect first and close second. close() opens a handshake with a
          // peer that is not there, and the browser sits in CLOSING until its
          // own timeout expires — waiting for that would reintroduce exactly
          // the stall the watchdog exists to end.
          end()
          try {
            ws.close(4000, 'no answer')
          } catch {
            // Already gone. The reconnect above is what mattered.
          }
        },
      })
      watchdog = local

      ws.onclose = end
    }

    open()

    /**
     * Re-examine the connection at the two moments it is most likely to have
     * died without saying so, and at which an operator is looking straight at
     * the pane: coming back to the tab, and the network coming back.
     *
     * The lease is renewed here as well, and this is not belt-and-braces. A
     * hidden tab has its timers clamped to roughly one firing a minute, so the
     * twenty-second renewal above becomes a sixty-second one, which races a
     * sixty-second TTL and loses about as often as it wins. The lease then
     * lapses while the socket stays perfectly healthy — so no reconnect happens,
     * nothing re-acquires, and the operator returns to a pane that has kept
     * every appearance of being theirs and silently refuses every keystroke.
     * Asking for it back on the way in is what closes that window; the request
     * is refused if somebody else has legitimately taken it, which is the
     * correct outcome and one the status frame then explains.
     */
    const revive = () => {
      if (stopped) return
      watchdog?.poke()
      const ws = wsRef.current
      if (ws?.readyState !== WebSocket.OPEN) return
      if (modeRef.current !== 'CONTROL' && modeRef.current !== 'COLLABORATE') return
      ws.send(encodeControl('heartbeat'))
      // Read the socket's own last word on who holds the lease rather than
      // this component's, which may be a minute stale for the same reason.
      if (!statusRef.current?.youAreController) ws.send(encodeControl('acquire'))
    }

    const onVisible = () => {
      if (document.visibilityState === 'visible') revive()
    }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('online', revive)
    // pageshow fires on a restore from the back/forward cache, where every
    // socket the page held was closed on the way in and no close event was
    // delivered to this handler.
    window.addEventListener('pageshow', revive)

    return () => {
      stopped = true
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('online', revive)
      window.removeEventListener('pageshow', revive)
      watchdog?.stop()
      watchdog = null
      if (heartbeat) clearInterval(heartbeat)
      if (retry) clearTimeout(retry)
      wsRef.current?.close(1000, 'unmounted')
      wsRef.current = null
    }
  }, [sessionId, clientId])

  /* -------------------------------------------------------------- focus */

  // Grant the keyboard when this pane actually becomes typeable — the same
  // moment it paints its accent frame — so that "the interface says you have the
  // keyboard" and "the browser agrees" cannot disagree.
  //
  // Rejected alternative: focusing on every canType transition regardless of the
  // slot. A grid mounts several panes in CONTROL at once, so the last status
  // frame to land would take the keyboard from the pane being driven.
  useEffect(() => {
    // Not while the transcript is up: it covers this terminal, and taking the
    // keyboard back would pull the operator out of the history they opened.
    if (!canType || !focusOnLive || historyOpen) return
    const el = document.activeElement as HTMLElement | null
    // Never pull the keyboard out of a field somebody is typing in — a filter
    // box, the search input above, a dialog. A button is fair game: that is the
    // case this exists for.
    if (
      el &&
      el !== document.body &&
      !hostRef.current?.contains(el) &&
      el.matches('input, textarea, select, [contenteditable]')
    ) {
      return
    }
    focusTerm()
  }, [canType, focusOnLive, historyOpen, focusTerm])

  // Cmd+S then a digit moves the keyboard here. Registered rather than found by
  // querying the DOM for a textarea: the chord asks the pane to focus itself, so
  // what "focused" means stays this component's business — it is the same call
  // the lease and the paste path already make.
  const registerChord = chord.register
  useEffect(() => registerChord(sessionId, focusTerm), [registerChord, sessionId, focusTerm])

  /* ------------------------------------------------------------ actions */

  const requestControl = () => {
    // Noted so the answer can be read. The server replies with a status frame
    // whether it granted control or refused it, and a refusal used to be
    // indistinguishable from a button that did nothing: the operator clicked,
    // the pane stayed an observer, and the reason — somebody holding the grant —
    // was only ever in the server's log.
    askedForControlRef.current = true
    wsRef.current?.send(encodeControl('acquire'))
    onModeChange?.('CONTROL')
    // The gesture that says "I want to type here" is also the one that must put
    // the keyboard here. Without this the operator held the lease and the focus
    // stayed on the button they had just pressed.
    focusTerm()
  }

  const releaseControl = () => {
    wsRef.current?.send(encodeControl('release'))
    onModeChange?.('OBSERVE')
  }

  /**
   * Cmd+I: the header button, on a key.
   *
   * One key for both directions rather than two, because it is one question —
   * "am I driving this?" — and the pane already answers it in three places at
   * once: the frame, the button's label and the strip below the header. A
   * second key for release would be a key an operator only needs when they have
   * forgotten which state they are in, which is exactly when they will press
   * the wrong one.
   *
   * A refusal is spoken rather than swallowed. The button for a pane somebody
   * else holds is disabled and says why in its tooltip; a key has no tooltip, so
   * pressing it on a held session has to produce the same sentence rather than
   * nothing at all.
   */
  const toggleControl = () => {
    if (observed) return
    if (canType) {
      releaseControl()
      return
    }
    // The same rule the button is disabled by, so Cmd+I and the control cannot
    // disagree about whether this pane may be driven.
    if (!mayTakeControl) {
      if (status?.controller != null) {
        showNote('error', t('terminal.controlHeldBy', { actor: status.controller }))
      }
      return
    }
    requestControl()
  }

  const closeHistory = useCallback(() => {
    setHistoryOpen(false)
    focusTerm()
  }, [focusTerm])

  const confirmPaste = () => {
    // Chunked, because a payload over the server's per-frame cap is a fatal
    // decode there rather than a refusal: the socket closes, and the frame is
    // already in the pending buffer, so it replays on every reconnect and flaps
    // the terminal for good. Each chunk carries its own sequence, so ordering
    // and exactly-once are unchanged.
    if (pendingPaste) for (const chunk of chunkInput(pendingPaste.text)) sendInput(chunk)
    setPendingPaste(null)
    focusTerm()
  }

  const cancelPaste = () => {
    setPendingPaste(null)
    focusTerm()
  }

  const openLink = () => {
    if (pendingLink) window.open(pendingLink, '_blank', 'noopener,noreferrer')
    setPendingLink(null)
    focusTerm()
  }

  const cancelLink = () => {
    setPendingLink(null)
    focusTerm()
  }

  /* ------------------------------------------------------------- render */

  // The frame's colour says what the session is doing; its *presence* still says
  // where the keyboard is. Two facts, two channels, so neither has to be given
  // up: an operator can see which pane they are typing into and, at the same
  // glance, whether the agent in it is still working.
  //
  // Without a caller-supplied answer the pane judges only what it can see for
  // itself — its own socket, and whether anything is reading the session.
  const paneActivity =
    activity ??
    sessionActivity({
      kind: status?.kind,
      connected: connection === 'open',
      attached: status?.attached,
      working: status?.workingNow,
    })
  const activityTone = sessionActivityTone(paneActivity)

  /**
   * The two keys the pane itself answers, caught where they land.
   *
   * On the pane's element rather than on `window`: there can be six of these on
   * a wall, and a window listener in each would have all six answer one press.
   * Bubbling from inside the terminal is what makes this the *focused* pane's
   * handler — xterm stops the key and lets the DOM event through (see the custom
   * key handler above), so the pane the keyboard is in is the one that hears it.
   */
  const onPaneKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (!e.metaKey) return
    if (e.key === 'i' || e.key === 'I') {
      e.preventDefault()
      toggleControl()
      return
    }
    // Back to the present. Only while the record is open, so the key is not a
    // silent no-op the rest of the time — and plain Esc keeps belonging to the
    // shell, which is why this one needs the modifier.
    if (e.key === 'Escape' && historyOpen) {
      e.preventDefault()
      closeHistory()
    }
  }

  return (
    // A named group rather than a bare div: in a grid layout there are up to four
    // of these on screen, and "terminal" repeated four times with nothing to tell
    // them apart is no better than no name at all. The description is where the
    // read-only reason lives, so an operator who lands on an observed session
    // hears why it refuses input instead of discovering it by typing.
    <div
      className={clsx(
        'k-term',
        // The one bold signal on the page: a pane the operator is actively
        // driving frames itself, so which of several panes has the keyboard is
        // legible across the room, not just by cursor.
        canType && 'k-term--live',
        `k-term--act-${activityTone}`,
      )}
      role="group"
      aria-label={`${t('live.terminal')} ${sessionId.slice(0, 8)}`}
      aria-describedby={hintId}
      onKeyDown={onPaneKeyDown}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {/* One header, and only one.

          The page used to draw a second one directly above this, and the two
          disagreed about who owned what: both showed a status dot, both showed
          the session kind, and "Devral" appeared twice — once as an option in a
          four-way mode switch that never left the browser, and once as this
          button, which is the only one the server answers. Merged here rather
          than there because everything except the session's name and the slot's
          own buttons is something only this socket knows. */}
      <header className="k-term__head">
        {/* The seat, when this pane is on a wall.
 
            Two characters at the head of the row, which is what the width
            budget can afford here — the session's name is what this row is for,
            and it has been squeezed to one letter before. Mono, because the
            number is a machine fact: the console assigned it from the wall's
            order, and an operator tells those from human text by the face.
 
            It brightens while the prefix is waiting rather than appearing then:
            a badge that arrives with the chord would move the header's contents
            under the operator's eyes at the moment they are trying to read a
            number off it. Rendered as text, so `prefers-reduced-motion` costs it
            nothing — there is no animation carrying the meaning. */}
        {seat > 0 && (
          <span
            className={clsx('k-term__seat', 'k-mono', chord.armed && 'k-term__seat--armed')}
            title={t('terminal.seat', { n: seat })}
          >
            {seat}
          </span>
        )}
        <Identity
          onClick={onInspect}
          pressed={inspecting}
          title={onInspect ? t('action.details') : undefined}
        >
          {/* One dot, and it answers the question the operator is actually
              asking: what is the agent doing. It used to report the socket
              instead, beside a second dot in the header above reporting the
              activity — and a broken socket already says so in words, in the
              banner below, rather than by turning a dot red. */}
          <StatusDot
            tone={activityTone}
            live={paneActivity === 'WORKING'}
            label={`${t(activityLabelKey(paneActivity))} · ${status?.state ?? connection}`}
          />
          {/* The name, in full, in every width. It used to sit behind an
              ellipsis at 26ch with the id and the path competing for the same
              room, and in a three-pane wall it lost: the header read "S..",
              "B.", "T." — three panes identified by their first letter. A name
              an operator cannot read is not an identity, so this one wraps
              rather than truncates and the path beside it gives way first.

              The id that used to sit here is gone. It was eight characters of
              a value nobody types and nobody reads aloud, spending the scarcest
              width in the console on it; the details drawer this identity opens
              carries the whole id, which is where a machine value belongs. */}
          {title && <span className="k-term__title">{title}</span>}
          {/* Only when it is not the default, for the same reason the session
              list stopped printing it: every session in this pool is MANAGED,
              so the badge said one word forever and spent the header's scarce
              width saying nothing. ADOPTED and OBSERVED change what this pane
              can do — an observed session has no controllable PTY behind it —
              and those are the ones worth a badge. */}
          {kind && kind !== 'MANAGED' && (
            <Badge tone={observed ? 'warn' : 'neutral'} subtle>
              {t(`terminal.sessionKind.${kind.toLowerCase()}` as never)}
            </Badge>
          )}
        </Identity>

        {/* The path sits beside the identity rather than inside it, and that is
            layout rather than taste. A wrapping flex row is cut into lines from
            what its items would measure *before* anything shrinks, so while the
            path was one of the identity's children the whole identity was
            measured at the path's full length — and a row that could not fit
            broke in two rather than shortening the one thing in it that reads
            perfectly well shortened. Out here it is its own item with a basis of
            zero, so the row is collected without it and it takes what is left.

            `title` rather than a tooltip component: the path is one string, the
            operator wants it on hover after a pause, and the platform already
            does exactly that with no markup, no portal and no timer. */}
        {status?.cwd && (
          <span className="k-term__cwd k-mono" title={status.cwd}>
            {/* Isolated, so the shortening drops the head and keeps the tail —
                see .k-term__cwd, where the whole of that argument lives. */}
            <bdi>{status.cwd}</bdi>
          </span>
        )}

        <div className="k-term__controls" ref={controlsRef}>
          {status?.recording && (
            <Badge tone="err" subtle>
              ● {t('terminal.recording')}
            </Badge>
          )}
          {/* History, search and copy mode behind one glyph.
              
              Each was a labelled button, and together they cost more of this row
              than the session's own name was left with. None of them is what an
              operator is looking at the header to find out — that is who holds
              the keyboard, which stays in the row below — and none is reached
              often enough to be worth a permanent place in the chrome.

              Copy mode is a toggle, so it is a menuitemcheckbox rather than a
              command: a plain entry would read as "enter copy mode" to somebody
              already in it. */}
          <OverflowMenu label={t('terminal.more')}>
            <OverflowItem onClick={() => setHistoryOpen('read')}>
              {t('terminal.history')}
            </OverflowItem>
            {/* The same overlay, opened on its search box. Searching *is* reading
                back, and there is one record to read: this used to open a second
                search over the emulator's buffer, which on the alternate screen
                holds the visible screen and nothing else — an operator looking
                for something said twenty turns ago was being handed a search of
                what was already on the screen in front of them. */}
            <OverflowItem onClick={() => setHistoryOpen('search')}>
              {t('terminal.search')}
            </OverflowItem>
            {/* Two labels, not one entry with a hidden checked state: the mode
                already announces itself in the standing strip below, so what the
                menu owes the operator is what clicking it will do. */}
            <OverflowItem onClick={() => (copy ? leaveCopy() : enterCopy())}>
              {copy ? t('copy.leave') : t('copy.mode')}
            </OverflowItem>
          </OverflowMenu>
          {/* An observed session gets no control here at all. The badge in the
              identity already says what it is, in words, and a second one
              saying the same thing was the last of the duplicated chrome. */}
          {!observed &&
            (canType ? (
              // Keyed, so React cannot reconcile the two branches onto one
              // <button> DOM node. It used to: focus survived the canType flip
              // onto a button whose label had silently become "detach", and the
              // operator's next Space released the lease they had just taken.
              <Button key="release" size="sm" variant="ghost" onClick={releaseControl}>
                {t('terminal.controlYours')} · {t('terminal.detach')}
              </Button>
            ) : (
              <Button
                key="take"
                size="sm"
                variant="primary"
                onClick={requestControl}
                disabled={!mayTakeControl}
                title={status?.controller != null ? t('terminal.controlHeld') : undefined}
              >
                {t('terminal.control')}
              </Button>
            ))}
          {actions}
        </div>
      </header>

      {/* Always rendered, so the group always has a description. On an observed
          session it is also visible, because "you cannot type here" is not a
          footnote — the warn tone alone would leave a colour-blind operator
          discovering it by typing into a void. */}
      <p
        id={hintId}
        className={observed ? 'k-term__notice' : 'k-visually-hidden'}
        role={observed ? 'note' : undefined}
      >
        {observed ? t('terminal.observedHint') : t('terminal.regionHint')}
      </p>

      {/* The stage: the terminal, and everything that appears over it.
          
          It exists so the canvas has one height. Every strip below used to sit
          in the pane's flow, so each time one appeared or went it took a row
          from the terminal — and a row here is not free: it re-fits the
          emulator, reflows a screen drawn with absolute cursor moves, and costs
          a tmux and a PTY resize on the far end. The reconnect banner does that
          *by itself*, on any blip, on a session nobody is touching, which is
          what an operator sees as a terminal that scrolls and redraws on its
          own. The rule was already written here for the drop overlay and the
          attachment note; these three had simply never been held to it. */}
      <div className="k-term__stage">
        {connection !== 'open' && (
          <div className="k-term__banner" role="status">
            {connection === 'closed' ? t('terminal.disconnected') : t('terminal.reconnecting')}
          </div>
        )}

        {/* A live socket to a session with no PTY client behind it. The work is
          still running; nothing is reading it. Said out loud, because the pane
          is otherwise indistinguishable from an idle terminal — which is how an
          operator ends up typing into a session that cannot hear them. */}
        {connection === 'open' && status?.attached === false && (
          <div className="k-term__banner" role="status">
            {t('terminal.sessionDetached')}
          </div>
        )}

        {copy && (
          /* A standing strip while the mode is on, naming the keys.
           Copy mode changes what every key does, so it cannot be a state the
           operator has to remember being in — and the keys are vim's, which is
           knowledge this console has no right to assume. */
          <div className="k-term__copy" role="status">
            <strong>{t('copy.mode')}</strong>
            <span className="k-term__copykeys k-mono">
              hjkl · w b · g G · v {t('copy.char')} · V {t('copy.line')} · y {t('copy.yank')} · esc
            </span>
            <span className="k-term__copywhat">
              {copy.anchor ? t(copy.linewise ? 'copy.line' : 'copy.char') : t('copy.cursor')}
            </span>
          </div>
        )}
        {yanked !== null && (
          <div className="k-term__yanked" role="status">
            {t('copy.taken', { n: yanked })}
          </div>
        )}
        {/* A session somebody else is driving is rendered at *their* geometry, so
          it can be wider or taller than this pane. Scrolled rather than
          reflowed: reflowing is precisely what the emulator must not do to a
          screen drawn with absolute cursor moves. */}
        <div
          className={clsx('k-term__canvas', (followsSession || adopting) && 'k-term__canvas--follows')}
          ref={hostRef}
        />
      </div>

      {/* The keys this device does not have. A real row rather than a float,
          which is the opposite of the rule below and for the same reason: it is
          drawn once at mount and never appears or disappears, so it costs one
          resize on the way in and none afterwards. Disabled rather than removed
          when the pane cannot be typed into, so taking the lease does not
          resize the terminal under the operator's hands. */}
      {coarse && (
        <KeyBar
          ctrl={ctrlLatch}
          alt={altLatch}
          onLatch={onLatch}
          onKey={onBarKey}
          enabled={canType}
        />
      )}

      {/* Both of these float over the canvas rather than taking a row of their
          own. A strip that appears and disappears in the flow resizes the
          terminal under the operator's hands — and a resize here is not free:
          it reflows the program's absolute cursor moves and costs a tmux and a
          PTY resize on the far end. */}
      {dragging && (
        <div className="k-term__drop" aria-hidden="true">
          <span className="k-term__drop-title">{t('terminal.dropImage')}</span>
          <span className="k-term__drop-hint">{t('terminal.dropImageHint')}</span>
        </div>
      )}

      {attachment && (
        <p className={clsx('k-term__attach', `k-term__attach--${attachment.tone}`)} role="status">
          {attachment.text}
        </p>
      )}

      {/* Covers the canvas rather than replacing it: the live stream keeps
          flowing underneath and the socket is untouched, so returning to the
          present is instant and nothing was missed while reading back. */}
      {/* One entry point, two sources, decided by state rather than by two buttons.
          The transcript covers a session while it exists; Kill drops it and leaves
          the recording, so for a session that has ended the transcript endpoint
          404s and the recording is the only history there is. The two never
          compete: the recording fills exactly the hole the transcript leaves. */}
      {historyOpen &&
        (ended ? (
          <RecordingOverlay
            sessionId={sessionId}
            onClose={closeHistory}
            focusSearch={historyOpen === 'search'}
          />
        ) : (
          <TranscriptOverlay
            sessionId={sessionId}
            onClose={closeHistory}
            focusSearch={historyOpen === 'search'}
          />
        ))}

      {/* Confirmations are inline rather than modal so they cannot be missed by
          an operator whose attention is on the output above them. */}
      {pendingPaste && (
        <div className="k-term__confirm" role="alertdialog" aria-label={t('terminal.largePaste')}>
          <span>{t('terminal.largePasteHint', { lines: pendingPaste.lines })}</span>
          <div className="k-term__confirm-actions">
            <Button size="sm" variant="ghost" onClick={cancelPaste}>
              {t('action.cancel')}
            </Button>
            <Button size="sm" variant="primary" onClick={confirmPaste}>
              {t('action.continue')}
            </Button>
          </div>
        </div>
      )}

      {pendingLink && (
        <div className="k-term__confirm" role="alertdialog" aria-label={t('terminal.linkConfirm')}>
          <span className="k-mono k-term__link">
            {t('terminal.linkConfirmHint', { url: pendingLink })}
          </span>
          <div className="k-term__confirm-actions">
            <Button size="sm" variant="ghost" onClick={cancelLink}>
              {t('action.cancel')}
            </Button>
            <Button size="sm" variant="primary" onClick={openLink}>
              {t('terminal.linkConfirm')}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

/** Reads the xterm palette from the design tokens, so the terminal follows the
 *  theme rather than carrying its own hard-coded colours. */
/**
 * The terminal's type size, from the same token the readability floor is derived
 * from.
 *
 * One source, because the two must not be able to disagree: the floor exists to
 * guarantee 80 columns fit, and it can only do that if it is scaled by the size
 * the emulator is actually drawing at.
 */
function readTermFontSize(): number {
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--k-term-font').trim()
  const n = Number.parseFloat(raw)
  return Number.isFinite(n) && n >= 9 && n <= 24 ? n : 12.5
}

function readXtermTheme() {
  const css = getComputedStyle(document.documentElement)
  const v = (name: string, fallback: string) => css.getPropertyValue(name).trim() || fallback
  // Every entry resolves from a token, and the pane re-runs this when the theme
  // changes — which is what makes the claim true rather than merely intended.
  // The fallbacks are only reached if a var read fails, so they are
  // kept in step with the current token values rather than the stale hexes they
  // used to carry (which would have surfaced, wrong, exactly when the tokens
  // were unavailable).
  return {
    background: v('--k-surface-sunken', '#080a0e'),
    foreground: v('--k-text', '#e6ebf2'),
    cursor: v('--k-accent', '#4dd8c0'),
    cursorAccent: v('--k-bg', '#0b0e13'),
    selectionBackground: v('--k-accent-line', 'rgba(77, 216, 192, 0.28)'),
    black: v('--k-bg', '#0b0e13'),
    red: v('--k-err', '#ff7676'),
    green: v('--k-ok', '#4ade80'),
    yellow: v('--k-warn', '#f5b544'),
    blue: v('--k-info', '#71a3ff'),
    magenta: v('--k-unknown', '#af98e5'),
    cyan: v('--k-accent', '#4dd8c0'),
    white: v('--k-text-secondary', '#a8b3c4'),
    brightBlack: v('--k-text-muted', '#8e98a7'),
    brightWhite: v('--k-text', '#e6ebf2'),
  }
}
